import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";

function createData(
  id,
  country,
  costOfLiving,
  safety,
  language,
  datingApps,
  womenHeight,
  religion,
  ethnicity
) {
  return {
    id,
    country,
    costOfLiving,
    safety,
    language,
    datingApps,
    womenHeight,
    religion,
    ethnicity,
  };
}

const rows = [
  createData(
    1,
    "Thailand",
    1368,
    2.05,
    "Thai",
    "Tinder, Bumble, Hinge",
    "153.7 cm (5 ft 1⁄2 in)",
    "92% buddhist, 5.4% muslim, 1.2% christian",
    "Thai 97.5%, Burmese 1.3%, other 1.1%, unspecified"
  ),
  createData(
    2,
    "Philippines",
    901,
    2.21,
    "Filipino, English",
    "Tinder, Bumble, Badoo",
    "151.8 cm (5 ft 0 in)",
    "88.5% christian, 6% muslim, 1.8% independent",
    "Tagalog 26%, Bisaya/Binisaya 14.3%, Ilocano 8%"
  ),
  createData(
    3,
    "Vietnam",
    1082,
    1.8,
    "Vietnamese",
    "Tinder, Falo, Boo",
    "156.2 cm (5 ft 1+1⁄2 in)",
    "73.7% Irreligion, 14.9% buddhism, 7.4% catholisism",
    "Kinh (Viet) 85.3%, Tay 1.9%, Thai 1.9%"
  ),
  createData(
    4,
    "China",
    1863,
    2.1,
    "Mandarin",
    "Tantan, Tinder, Badoo",
    "155.8 cm (5 ft 1+1⁄2 in)",
    "33.4% buddhism, 25.2% no religion, 19.6% taoism",
    "Han Chinese 91.1%, ethnic minorities 8.9%"
  ),
  createData(
    5,
    "Japan",
    2438,
    1.52,
    "Japanese",
    "With, Pairs, Bumble",
    "158.6 cm (5 ft 2+1⁄2 in)",
    "48.6% shintoism, 46% buddhism, 1.5% christianity",
    "Japanese 97.5%, Chinese 0.6%, Vietnam 0.4%"
  ),
  createData(
    6,
    "Colombia",
    1062,
    2.89,
    "Spanish",
    "Tinder, Bumble, iHappy",
    "158.7 cm (5 ft 2+1⁄2 in)",
    "64.8% catholic, 18.2% other christian, no religion 16.6%",
    "Mestizo and White 87.6%, Afro-Colombian 6.8%, Indigenous 4.3%"
  ),
  createData(
    7,
    "Brazil",
    1009,
    2.59,
    "Portugese",
    "Tinder, Badoo, Bumble",
    "158.8 cm (5 ft 2+1⁄2 in)",
    "54.2% catholic, evangelist 20.5%, 3.7% pentecostal evangelist",
    "mixed 45.3%, White 43.5%, Black 10.2%"
  ),
  createData(
    8,
    "Dominican Republic",
    1211,
    2.16,
    "Spanish",
    "Evermatch, iHappy, Tinder",
    "159 cm (5 ft 2+1⁄2 in)",
    "59.2% catholism, 19% protestantism,16.6% unaffiliated",
    "mixed 70.4%, Black 15.8%, White 13.5%"
  ),
  createData(
    9,
    "Mexico",
    1415,
    2.79,
    "Spanish",
    "Tinder, Bumble, Badoo",
    "159 cm (5 ft 2+1⁄2 in)",
    "77.8% catholicism, 11.7% protestantism, 1.8% other christian",
    "Mestizo 62%, Indigenous 21%, Indigenous-Spanish 7%"
  ),
  createData(
    10,
    "Venezuela",
    1154,
    2.82,
    "Spanish",
    "iHappy, Tinder, #Dating",
    "160.0 cm (5 ft 3 in)",
    "63.6% catholicism, 21.7% evangelist, 1.3% afroamerican cults",
    "Multiracial 51.6%, White 43.6%, African 3.6%"
  ),
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "country",
    numeric: false,
    disablePadding: false,
    label: "Country 🌍",
  },
  {
    id: "costOfLiving",
    numeric: true,
    disablePadding: false,
    label: "Single Monthly Cost of Living (USD) 💵",
  },
  {
    id: "safety",
    numeric: true,
    disablePadding: false,
    label: "Safety (Overall GPI Score) ❌",
  },
  {
    id: "language",
    numeric: false,
    disablePadding: false,
    label: "Language 🗣️",
  },
  {
    id: "datingApps",
    numeric: false,
    disablePadding: false,
    label: "Popular Dating Apps (Top 3) 💘",
  },
  {
    id: "womenHeight",
    numeric: false,
    disablePadding: false,
    label: "Average Women Height 🧍‍♀️",
  },
  {
    id: "religion",
    numeric: false,
    disablePadding: false,
    label: "Religion (Top 3) 🙏",
  },
  {
    id: "ethnicity",
    numeric: false,
    disablePadding: false,
    label: "Ethnicity (Top 3) 👩",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default function EnhancedTable() {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage]
  );

  return (
    <Box sx={{ width: "100%"}}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                    >
                      {row.country}
                    </TableCell>
                    <TableCell align="right">{row.costOfLiving}</TableCell>
                    <TableCell align="right">{row.safety}</TableCell>
                    <TableCell align="left">{row.language}</TableCell>
                    <TableCell align="left">{row.datingApps}</TableCell>
                    <TableCell align="left">{row.womenHeight}</TableCell>
                    <TableCell align="left">{row.religion}</TableCell>
                    <TableCell align="left">{row.ethnicity}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
